/**
 * CR325: Bounce Log Panel
 *
 * Shows the last 5 editor events, event count, and
 * Copy / Clear buttons. Lives inside the debug strip area.
 */

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Copy, Check, Trash2, ShieldCheck, ShieldOff, RefreshCw } from "lucide-react";
import {
  getLastEvents,
  getEventCount,
  copyEvts,
  clearEvts,
  type EditorEvent,
} from "@/lib/debug/dealCardEditorEvents";

interface BounceLogPanelProps {
  stabilityMode: boolean;
  onToggleStabilityMode: () => void;
  onManualRefresh: () => void;
}

export function BounceLogPanel({
  stabilityMode,
  onToggleStabilityMode,
  onManualRefresh,
}: BounceLogPanelProps) {
  const [lastEvents, setLastEvents] = useState<EditorEvent[]>([]);
  const [count, setCount] = useState(0);
  const [copied, setCopied] = useState(false);

  // Poll for new events every 500ms (lightweight — just reads array length)
  useEffect(() => {
    const tick = () => {
      setLastEvents(getLastEvents(5));
      setCount(getEventCount());
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, []);

  const handleCopy = useCallback(async () => {
    const ok = await copyEvts();
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, []);

  const handleClear = useCallback(() => {
    clearEvts();
    setLastEvents([]);
    setCount(0);
  }, []);

  // Format relative time (ms from page load)
  const fmtT = (t: number) => {
    if (t < 1000) return `${t}ms`;
    return `${(t / 1000).toFixed(1)}s`;
  };

  return (
    <div className="text-[10px] font-mono bg-slate-900 text-slate-300 px-3 py-1.5 rounded space-y-1">
      {/* Top row: controls */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Stability Mode */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleStabilityMode}
          className={`h-5 px-1.5 text-[9px] gap-1 ${
            stabilityMode
              ? "text-green-400 hover:text-green-300"
              : "text-amber-400 hover:text-amber-300"
          }`}
          title={stabilityMode ? "Stability Mode ON — background refresh disabled" : "Stability Mode OFF — background refresh enabled"}
        >
          {stabilityMode ? (
            <><ShieldCheck className="h-3 w-3" /> Stability ON</>
          ) : (
            <><ShieldOff className="h-3 w-3" /> Stability OFF</>
          )}
        </Button>

        {/* Manual Refresh */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onManualRefresh}
          className="h-5 px-1.5 text-[9px] text-slate-400 hover:text-white gap-1"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </Button>

        <div className="flex-1" />

        {/* Event count */}
        <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 border-slate-600 text-slate-400">
          {count} evts
        </Badge>

        {/* Copy */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-5 px-1.5 text-[9px] text-slate-400 hover:text-white gap-1"
        >
          {copied ? (
            <><Check className="h-3 w-3" /> Copied</>
          ) : (
            <><Copy className="h-3 w-3" /> Copy Bounce Log</>
          )}
        </Button>

        {/* Clear */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="h-5 px-1.5 text-[9px] text-slate-400 hover:text-white gap-1"
        >
          <Trash2 className="h-3 w-3" /> Clear
        </Button>
      </div>

      {/* Last 5 events */}
      {lastEvents.length > 0 && (
        <div className="space-y-0.5 border-t border-slate-800 pt-1">
          {lastEvents.map((evt) => (
            <div key={evt.seq} className="flex gap-2 items-baseline">
              <span className="text-slate-600 w-10 text-right shrink-0">
                {fmtT(evt.t)}
              </span>
              <span className={getEventColor(evt.type)}>
                {evt.type}
              </span>
              {evt.dealTypeKey && (
                <span className="text-cyan-400/60">{evt.dealTypeKey}</span>
              )}
              {evt.hydrating !== undefined && (
                <span className={evt.hydrating ? "text-yellow-400" : "text-slate-600"}>
                  h={evt.hydrating ? "Y" : "n"}
                </span>
              )}
              {evt.templateDirty !== undefined && (
                <span className={evt.templateDirty ? "text-red-400" : "text-green-400/60"}>
                  td={evt.templateDirty ? "Y" : "n"}
                </span>
              )}
              {evt.savedHash && (
                <span className="text-purple-400/60">s={evt.savedHash.slice(0, 4)}</span>
              )}
              {evt.effectiveHash && (
                <span className="text-blue-400/60">e={evt.effectiveHash.slice(0, 4)}</span>
              )}
              {evt.note && (
                <span className="text-slate-500 truncate max-w-[200px]">{evt.note}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function getEventColor(type: string): string {
  if (type.startsWith("hydrate")) return "text-yellow-400";
  if (type.startsWith("apply")) return "text-cyan-400";
  if (type.startsWith("dirty")) return "text-red-400";
  if (type.startsWith("draft_persist")) return "text-orange-400";
  if (type.startsWith("save")) return "text-green-400";
  if (type.includes("violation")) return "text-red-500 font-bold";
  if (type.includes("stability")) return "text-green-300";
  return "text-slate-400";
}
